import { FC, Fragment, ReactNode, useMemo } from 'react';

import { CampaignProcess } from 'utils/constants/campaign';

import { AdvertisementBannerCheckbox } from 'components/common/AdvertisementBannerCheckbox/AdvertisementBannerCheckbox';
import { BannerImg } from 'components/common/BannerImg/BannerImg';
import { BannerVideo } from 'components/common/BannerVideo/BannerVideo';
import { AdAge } from 'components/CpcFlexibleCreate/CpcForm/AdAge/AdAge';
import { AdBudget } from 'components/CpcFlexibleCreate/CpcForm/AdBudget/AdBudget';
import { AdCampaignInformation } from 'components/CpcFlexibleCreate/CpcForm/AdCampaignInformation/AdCampaignInformation';
import { AdCampaignTitle } from 'components/CpcFlexibleCreate/CpcForm/AdCampaignTitle/AdCampaignTitle';
import { AdGender } from 'components/CpcFlexibleCreate/CpcForm/AdGender/AdGender';
import { AdLanguages } from 'components/CpcFlexibleCreate/CpcForm/AdLanguages/AdLanguages';
import { AdProfessions } from 'components/CpcFlexibleCreate/CpcForm/AdProfessions/AdProfessions';
import { AdRegions } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/AdRegions';
import { AdSalaries } from 'components/CpcFlexibleCreate/CpcForm/AdSalaries/AdSalaries';
import { AdSources } from 'components/CpcFlexibleCreate/CpcForm/AdSources/AdSources';
import { BannerText } from 'components/CpcFlexibleCreate/CpcForm/BannerText/BannerText';
import { BannerTitle } from 'components/CpcFlexibleCreate/CpcForm/BannerTitle/BannerTitle';
import { ErirClassification } from 'components/CpcFlexibleCreate/CpcForm/ErirClassification/ErirClassification';
import { VacancyOrLinkChoice } from 'components/CpcFlexibleCreate/CpcForm/VacancyOrLinkChoice/VacancyOrLinkChoice';

import { selectCampaignProcess } from 'store/models/createCampaign';
import { useSelector } from 'store/utils';

interface ReturnData {
    stepContent: Record<number, ReactNode>;
}

interface StepComponents {
    [stepIndex: number]: Array<{
        Component: FC;
        props?: Record<string, unknown>;
        processes?: CampaignProcess[];
    }>;
}

const STEP_COMPONENTS: StepComponents = {
    0: [
        { Component: VacancyOrLinkChoice, processes: [CampaignProcess.Create, CampaignProcess.Copy] },
        { Component: ErirClassification, processes: [CampaignProcess.Create, CampaignProcess.Copy] },
        {
            Component: BannerTitle,
            processes: [CampaignProcess.Create, CampaignProcess.Copy],
        },
        {
            Component: BannerText,
            processes: [CampaignProcess.Create, CampaignProcess.Copy],
        },
        {
            Component: BannerImg,
            processes: [CampaignProcess.Create, CampaignProcess.Copy],
        },
        {
            Component: BannerVideo,
            processes: [CampaignProcess.Create, CampaignProcess.Copy],
        },
        {
            Component: AdvertisementBannerCheckbox,
            processes: [CampaignProcess.Create, CampaignProcess.Copy],
        },
    ],
    1: [
        { Component: AdCampaignTitle, processes: [CampaignProcess.Edit] },
        { Component: AdRegions },
        { Component: AdProfessions },
        { Component: AdGender },
        { Component: AdAge },
        { Component: AdSalaries },
        { Component: AdLanguages },
    ],
    2: [{ Component: AdSources }, { Component: AdBudget }, { Component: AdCampaignInformation }],
};

const PROCESS_STEP_MAPPING = {
    [CampaignProcess.Create]: [0, 1, 2],
    [CampaignProcess.Edit]: [1, 2],
    [CampaignProcess.Copy]: [0, 1, 2],
};

export const useCpcFormData = (): ReturnData => {
    const campaignProcess = useSelector(selectCampaignProcess);

    return useMemo(() => {
        const stepIndices = PROCESS_STEP_MAPPING[campaignProcess];

        const stepContent: Record<number, ReactNode> = {};

        stepIndices.forEach((step, index) => {
            const components = STEP_COMPONENTS[step] || [];
            const filteredComponents = components.filter(
                (config) => !config.processes || config.processes.includes(campaignProcess)
            );

            stepContent[index] = (
                <Fragment key={index}>
                    {filteredComponents.map(({ Component, props = {} }, j) => (
                        <Component key={j} {...props} />
                    ))}
                </Fragment>
            );
        });

        return { stepContent };
    }, [campaignProcess]);
};
